import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EditPropertyData } from '@/schema/properties';

interface ContractInformationProps {
  form: UseFormReturn<EditPropertyData>;
}

export function ContractInformation({ form }: ContractInformationProps) {
  const { control } = form;
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  return (
    <div className="space-y-4">
      <h3 className={cn('text-lg font-medium', isRTL && 'text-right')}>
        {t('properties.edit.contractInfo.title')}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="contract_start" className={cn(isRTL && 'text-right')}>
            {t('properties.edit.contractInfo.contractStart')}
          </Label>
          <Controller
            name="contract_start"
            control={control}
            render={({ field }) => (
              <Input
                id="contract_start"
                type="date"
                {...field}
                className={cn(isRTL ? 'text-right' : 'text-left')}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            )}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="contract_end" className={cn(isRTL && 'text-right')}>
            {t('properties.edit.contractInfo.contractEnd')}
          </Label>
          <Controller
            name="contract_end"
            control={control}
            render={({ field }) => (
              <Input
                id="contract_end"
                type="date"
                {...field}
                className={cn(isRTL ? 'text-right' : 'text-left')}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            )}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="photoBase64" className={cn(isRTL && 'text-right')}>
          {t('properties.edit.contractInfo.logoImage')}
        </Label>
        <Controller
          name="photoBase64"
          control={control}
          render={({ field }) => (
            <Input
              id="logoUrl"
              type="file"
              accept="image/*"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) {
                  // Create object URL for preview
                  const imageUrl = URL.createObjectURL(file);
                  field.onChange(imageUrl);
                }
              }}
              className="cursor-pointer"
            />
          )}
        />
      </div>
    </div>
  );
}
